import { Button, Modal, Stack, Text, tokens } from '@hotjar/hotsauce';
import type { IconSetDefinitions } from '@hotjar/hotsauce/build/components/Icon/icons';
import { ArrayTranslation, useTranslationWithPrefix } from '@hotjar/translations';
import styled from '@emotion/styled';
import type { EnhancedProduct } from '@hotjar/product-catalog';
import { getProductAndPlanFromSku } from '@hotjar/product-catalog';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { createGlobalModal } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { useOwnedAccount } from '@hotjar/account';
import { extractAccountGroupingProps } from '@hotjar/webapp/tracking/utils';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import { closedModal, loadedModal, trackClick } from '@hotjar/tracking';
import { MissingProductFeatures } from './MissingProductFeatures';
import type { ProductAllowance } from '~/subscription-management/api/common/model/subscription';

const S = {
  ModalBody: styled(Modal.Body)`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
  `,
  ModalBodyInner: styled.div`
    display: flex;
    flex-direction: column;
    max-width: 800px;
    gap: ${tokens.spacer.x5};
  `,
  Intro: styled.div`
    grid-column: 1 / 3;
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacer.x3};
  `,
  ProductList: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: ${tokens.spacer.x3};
    row-gap: ${tokens.spacer.x4};
  `,
  Alerts: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${tokens.spacer.x3};
  `,
  Alert: styled.div`
    background-color: ${tokens.color.bg_info};
    padding: ${tokens.spacer.x2};
    border-radius: ${tokens.radius.m};
  `,
  Button: styled(Button)`
    width: fit-content;
  `,
};

interface Alert {
  /* eslint-disable react/no-unused-prop-types */
  title: string;
  description: string;
  link: string;
  href: string;
  icon: keyof IconSetDefinitions | undefined;
  /* eslint-enable react/no-unused-prop-types */
}

interface Props {
  currentProducts: ProductAllowance[];
  subscriptionEndDate?: string;
}

const MODAL_NAME = 'Change plan confirmation multi sku churn deflection';

export function ChurnDeflectionModal({
  currentProducts,
  subscriptionEndDate,
  onSubmit,
  onCancel: onClose,
}: Props & GlobalModalProps<void, void>): JSX.Element {
  const { t: tChurn } = useTranslationWithPrefix('checkout:churn.deflection-modal');

  const { data: account } = useOwnedAccount();

  useEffectOnceWithDependencies(
    () => {
      loadedModal(MODAL_NAME, extractAccountGroupingProps(account));
    },
    [account],
    () => account !== undefined,
  );

  function handleClose() {
    closedModal(MODAL_NAME, extractAccountGroupingProps(account));
    onClose();
  }

  function handleSubmit() {
    trackClick({
      name: 'Continue to downgrade',
      uiPosition: MODAL_NAME,
      groupData: extractAccountGroupingProps(account),
    });
    onSubmit();
  }

  const getHighestPlan = (
    productName: EnhancedProduct['name'],
  ): 'plus' | 'businessOrHigher' | null => {
    const paidProducts = currentProducts
      .map(({ sku }) => getProductAndPlanFromSku(sku))
      .filter(({ product }) => product.name === productName)
      .filter(({ plan }) => plan.name !== 'basic');

    if (paidProducts.length === 0) {
      return null;
    }

    const hasBusinessOrHigher = paidProducts.some(({ plan }) => plan.planTier > 1);

    return hasBusinessOrHigher ? 'businessOrHigher' : 'plus';
  };

  const highestObservePlan = getHighestPlan('observe');
  const highestAskPlan = getHighestPlan('ask');

  return (
    <Modal
      isOpen
      contentLabel={tChurn('title')}
      size="fullscreen"
      onClose={handleClose}
      appElementSelector="#app-container"
    >
      <S.ModalBody>
        <S.ModalBodyInner>
          {/* Intro */}
          <S.Intro>
            <Text textType="header4" as="h1">
              {tChurn('title')}
            </Text>

            <Stack direction="column" spacing="x0pt5">
              <Text>{tChurn('thanks')}</Text>
              <Text>{tChurn('lose-access', { date: subscriptionEndDate })}</Text>
            </Stack>
          </S.Intro>

          <S.ProductList>
            {highestObservePlan !== null && (
              <MissingProductFeatures product="observe" plan={highestObservePlan} />
            )}
            {highestAskPlan !== null && (
              <MissingProductFeatures product="ask" plan={highestAskPlan} />
            )}
          </S.ProductList>

          <S.Alerts>
            <ArrayTranslation
              t={tChurn}
              i18nKey="alerts"
              mapperFunction={({ title, href, description, link, icon }: Alert) => (
                <S.Alert key={title}>
                  <Stack spacing="x3" direction="column">
                    <Stack spacing="x1" direction="column">
                      <Text textType="subtitle2" as="span">
                        {title}
                      </Text>
                      <Text textType="body2" textColor="text_light">
                        {description}
                      </Text>
                    </Stack>
                    <S.Button
                      as="a"
                      icon={icon}
                      kind="secondary"
                      href={href}
                      size="s"
                      isExternal
                      onClick={() =>
                        trackClick({
                          name: link,
                          uiPosition: MODAL_NAME,
                          groupData: extractAccountGroupingProps(account),
                        })
                      }
                    >
                      {link}
                    </S.Button>
                  </Stack>
                </S.Alert>
              )}
            />
          </S.Alerts>
        </S.ModalBodyInner>
      </S.ModalBody>

      <Modal.Footer>
        <Button kind="danger" onClick={handleSubmit}>
          {tChurn('actions.continue-to-downgrade')}
        </Button>
        <Button kind="ghost" onClick={handleClose}>
          {tChurn('actions.keep-plans')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export const showChurnDeflectionModal = createGlobalModal<Props, void, void>(ChurnDeflectionModal);
